import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUserPickemsForPeriod } from '@/api/query/useLeaderboard';
import { breakdownGridClass, DEFAULT_VISIBLE_CURRENCIES } from './currencyColumns';

interface UserPickemsListProps {
  userId: string;
  period: 'today' | 'week' | 'month' | 'year';
  weekOffset?: number;
  leagueCode?: string;
  isCurrentUser?: boolean;
}

export const UserPickemsList: React.FC<UserPickemsListProps> = ({
  userId,
  period,
  weekOffset = 0,
  leagueCode,
  isCurrentUser = false,
}) => {
  const { t } = useTranslation();
  const { data, isLoading, error } = useUserPickemsForPeriod(
    userId,
    period,
    weekOffset,
    leagueCode
  );

  if (isLoading) {
    return (
      <div className={`text-sm ${isCurrentUser ? 'text-white/70' : 'text-text-secondary'}`}>
        Loading pickems...
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-sm ${isCurrentUser ? 'text-white/70' : 'text-text-secondary'}`}>
        Failed to load pickems
      </div>
    );
  }

  if (!data?.pickems || data.pickems.length === 0) {
    return (
      <div className={`text-sm ${isCurrentUser ? 'text-white/70' : 'text-text-secondary'}`}>
        No pickems found for this period
      </div>
    );
  }

  // DKPLAT-618: only render Cash/Crowns columns when a pick'em in this period
  // actually uses them (SkyPicks pays Other only). Grid tracks adapt to match.
  const currencies = data.visibleCurrencies ?? DEFAULT_VISIBLE_CURRENCIES;
  const gridClass = breakdownGridClass(currencies);

  return (
    <div>
      {/* Column header */}
      <div
        className={`grid ${gridClass} gap-2 text-xs font-medium mb-2 pb-2 border-b ${
          isCurrentUser ? 'border-white/20' : 'border-border/20'
        }`}
      >
        <span>{t('leaderboard.pickem')}</span>
        <span className="text-center">{t('leaderboard.rank')}</span>
        {currencies.cash && <span>Cash</span>}
        {currencies.crowns && <span className="hidden sm:block">Crowns</span>}
        <span className="hidden sm:block">Other</span>
        <span className="text-center">{t('leaderboard.points')}</span>
      </div>

      <div className="space-y-1">
        {data.pickems.map(pickem => (
          <div
            key={pickem.pickem_id}
            className={`grid ${gridClass} gap-2 text-sm py-2 border-b ${
              isCurrentUser ? 'border-white/10' : 'border-border/10'
            } last:border-0`}
          >
            {/* Pickem Title */}
            <span className={`truncate ${isCurrentUser ? 'text-white' : 'text-text-primary'}`}>
              {pickem.pickem_title}
            </span>

            {/* Rank */}
            <span
              className={`text-center ${isCurrentUser ? 'text-white/90' : 'text-text-secondary'}`}
            >
              {pickem.rank !== null ? `#${pickem.rank}` : '-'}
            </span>

            {/* Cash - always visible when the currency is in use */}
            {currencies.cash && (
              <span
                className={`font-bold text-xs ${isCurrentUser ? 'text-white' : 'text-green-500'}`}
              >
                {pickem.prize?.cash && pickem.prize.cash > 0 ? (
                  `$${pickem.prize.cash.toLocaleString()}`
                ) : (
                  <span className={isCurrentUser ? 'text-white/40' : 'text-text-secondary/40'}>
                    -
                  </span>
                )}
              </span>
            )}

            {/* Crowns - hidden on mobile */}
            {currencies.crowns && (
              <span
                className={`hidden sm:block text-xs ${
                  isCurrentUser ? 'text-white/80' : 'text-yellow-500'
                }`}
              >
                {pickem.prize?.crowns && pickem.prize.crowns > 0 ? (
                  `${pickem.prize.crowns.toLocaleString()}c`
                ) : (
                  <span className={isCurrentUser ? 'text-white/40' : 'text-text-secondary/40'}>
                    -
                  </span>
                )}
              </span>
            )}

            {/* Other - hidden on mobile */}
            <span
              className={`hidden sm:block text-xs truncate ${
                isCurrentUser ? 'text-white/80' : 'text-blue-400'
              }`}
              title={pickem.prize?.other || ''}
            >
              {pickem.prize?.other ? (
                pickem.prize.other
              ) : (
                <span className={isCurrentUser ? 'text-white/40' : 'text-text-secondary/40'}>
                  -
                </span>
              )}
            </span>

            {/* Points */}
            <span
              className={`text-center font-medium ${isCurrentUser ? 'text-white' : 'text-text-primary'}`}
            >
              {pickem.points}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};
